import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Fab,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Avatar,
  Tooltip,
  Badge,
  Button,
} from '@mui/material';
import {
  SmartToy,
  Close,
  Send,
  RestaurantMenu,
  TrendingUp,
  LocalFireDepartment,
  Spa,
  EggAlt,
  Icecream,
  LocalBar,
  FitnessCenter,
  LunchDining,
  ChildCare,
  TempleBuddhist,
  MoneyOff,
  ShoppingCart,
  MenuBook,
  People,
  Person,
  TableRestaurant,
  BarChart,
  Payment,
  HelpOutline,
  AddCircle,
  CheckCircle,
  Cancel,
  Business,
  Settings,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../services/api';
import { MenuCategory } from '../types';

// ── Types ─────────────────────────────────────────────────────────────────────
interface RecommendedItem {
  id: string;
  name: string;
  price: number;
  tags?: string;
  score: number;
}

interface ChipOption { label: string; value: string; }

interface Message {
  id: string;
  role: 'user' | 'bot';
  text: string;
  intent?: string;
  items?: RecommendedItem[];
  chipOptions?: ChipOption[];   // inline selectable chips (e.g. category list)
  ts: Date;
}

type AddMenuStep = 'idle' | 'ask_name' | 'ask_price' | 'ask_category' | 'ask_tags' | 'confirm';

interface AddMenuDraft {
  name: string;
  price: number;
  categoryId: string;
  categoryName: string;
  tags: string;
}

interface ChatbotFABProps {
  isDark: boolean;
  currentRestaurantId?: string;
  userRole?: string;
  userName?: string;
}

// ── Static data ───────────────────────────────────────────────────────────────
const FOOD_CHIPS = [
  { label: 'Popular',   icon: <TrendingUp sx={{ fontSize: 14 }} />,          msg: 'Show me the most popular dishes' },
  { label: 'Spicy 🌶️',  icon: <LocalFireDepartment sx={{ fontSize: 14 }} />, msg: 'Show me spicy dishes' },
  { label: 'Veg',       icon: <Spa sx={{ fontSize: 14 }} />,                  msg: 'Show vegetarian options' },
  { label: 'Non-Veg',   icon: <EggAlt sx={{ fontSize: 14 }} />,               msg: 'Show non-veg options' },
  { label: 'Desserts',  icon: <Icecream sx={{ fontSize: 14 }} />,             msg: 'Show me desserts' },
  { label: 'Drinks',    icon: <LocalBar sx={{ fontSize: 14 }} />,             msg: 'Show me drinks' },
  { label: 'Healthy',   icon: <FitnessCenter sx={{ fontSize: 14 }} />,        msg: 'Healthy food options' },
  { label: 'Combos',    icon: <LunchDining sx={{ fontSize: 14 }} />,          msg: 'Show combo meal deals' },
  { label: 'Kids',      icon: <ChildCare sx={{ fontSize: 14 }} />,            msg: 'Kids menu items' },
  { label: 'Jain',      icon: <TempleBuddhist sx={{ fontSize: 14 }} />,       msg: 'Jain food no onion garlic' },
  { label: 'Budget',    icon: <MoneyOff sx={{ fontSize: 14 }} />,             msg: 'Budget friendly options' },
  { label: 'Recommend', icon: <RestaurantMenu sx={{ fontSize: 14 }} />,       msg: 'Recommend something for me' },
];

const GUIDE_CHIPS: { label: string; icon: React.ReactNode; msg: string; action?: string; roles?: string[] }[] = [
  { label: 'Create Order', icon: <ShoppingCart sx={{ fontSize: 14 }} />,   msg: 'How to create an order?',    roles: ['Admin', 'SuperAdmin', 'Staff', 'Waiter', 'Cashier', 'Manager'] },
  { label: '➕ Add Item',   icon: <AddCircle sx={{ fontSize: 14 }} />,      msg: '', action: '__ADD_MENU__',  roles: ['Admin', 'SuperAdmin'] },
  { label: 'Add Staff',    icon: <People sx={{ fontSize: 14 }} />,          msg: 'How to add staff?',          roles: ['Admin', 'SuperAdmin'] },
  { label: 'Tables',       icon: <TableRestaurant sx={{ fontSize: 14 }} />, msg: 'How to manage tables?',      roles: ['Admin', 'SuperAdmin', 'Staff', 'Waiter', 'Manager'] },
  { label: 'Reports',      icon: <BarChart sx={{ fontSize: 14 }} />,        msg: 'How to view reports?',       roles: ['Admin', 'SuperAdmin'] },
  { label: 'Payment',      icon: <Payment sx={{ fontSize: 14 }} />,         msg: 'How to process payment?',    roles: ['Admin', 'SuperAdmin', 'Staff', 'Cashier', 'Manager'] },
  { label: 'Menu Guide',   icon: <MenuBook sx={{ fontSize: 14 }} />,        msg: 'How to add menu items?',     roles: ['Admin', 'SuperAdmin'] },
  { label: 'Features',     icon: <HelpOutline sx={{ fontSize: 14 }} />,     msg: 'What features are available?' },
  { label: 'Tenants 🏢',   icon: <Business sx={{ fontSize: 14 }} />,        msg: 'How to manage tenants/restaurants?', roles: ['SuperAdmin'] },
  { label: 'Pricing 💳',   icon: <Settings sx={{ fontSize: 14 }} />,        msg: 'How to configure pricing plans?',    roles: ['SuperAdmin'] },
];

const INTENT_COLORS: Record<string, string> = {
  RECOMMEND: '#6366f1', SPICY: '#ef4444', VEG: '#22c55e', NONVEG: '#f97316',
  BUDGET: '#eab308', POPULAR: '#f59e0b', DESSERT: '#ec4899', DRINKS: '#06b6d4',
  HEALTHY: '#10b981', COMBO: '#8b5cf6', KIDS: '#3b82f6', JAIN: '#14b8a6',
  HOW_ORDER: '#6366f1', HOW_MENU: '#f97316', HOW_STAFF: '#8b5cf6',
  HOW_TABLE: '#06b6d4', HOW_REPORT: '#eab308', HOW_PAYMENT: '#22c55e',
  FEATURES: '#ec4899', ADD_MENU: '#10b981', UNKNOWN: '#94a3b8',
};

const PANEL_WIDTH = 420;

// ── Component ─────────────────────────────────────────────────────────────────
const ChatbotFAB: React.FC<ChatbotFABProps> = ({
  isDark,
  currentRestaurantId,
  userRole = '',
  userName = 'there',
}) => {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [unreadCount, setUnread]  = useState(0);
  const [showFoodChips, setShowFood] = useState(true);
  const [showFoodPanel, setShowFoodPanel] = useState(false);

  // Add-menu flow state
  const [addStep, setAddStep]     = useState<AddMenuStep>('idle');
  const [draft, setDraft]         = useState<AddMenuDraft>({ name: '', price: 0, categoryId: '', categoryName: '', tags: '' });
  const [categories, setCategories] = useState<MenuCategory[]>([]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);

  const bg      = isDark ? '#0f172a' : '#ffffff';
  const surface = isDark ? '#1e293b' : '#f8fafc';
  const border  = isDark ? '#334155' : '#e2e8f0';
  const textPri = isDark ? '#f1f5f9' : '#0f172a';
  const textMut = isDark ? '#94a3b8' : '#64748b';
  const accent  = '#6366f1';
  const green   = '#10b981';

  const canAddMenu = userRole === 'Admin' || userRole === 'SuperAdmin';

  // ── Welcome message ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && messages.length === 0) {
      const h = new Date().getHours();
      const greet = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
      const name = userName && userName !== 'there' ? userName : 'there';
      
      let welcomeText = `${greet}, **${name}**! 👋 I'm **Maya**, your MenuMaster AI Assistant.\n\nI can help you:\n🍽️ **Food** — recommend, find spicy/veg/desserts & more\n📚 **Guide** — how to use any feature\n${canAddMenu ? '➕ **Add Menu Items** — directly from chat!\n' : ''}\nWhat can I do for you today?`;
      
      if (userRole === 'SuperAdmin') {
        welcomeText = `${greet}, **Super Admin**! 👑 👋 I'm **Maya**, your MenuMaster System Assistant.\n\nHere's what I can help you with:\n🏢 **Tenants** — Manage restaurants, view billing, configure status\n💳 **Pricing & Plans** — Configure system-wide pricing structures\n📊 **Global Revenue** — Access system reports and revenue insights\n\nHow can I help you manage the system today?`;
      } else if (userRole === 'Admin') {
        welcomeText = `${greet}, **Admin**! 💼 👋 I'm **Maya**, your MenuMaster AI Assistant.\n\nI can help you with:\n🍽️ **Food** — Recommendations, spicy/veg lists & more\n➕ **Add Menu Items** — Directly from the chat (type "add menu item")\n👥 **Staff & Tables** — Guidance on tables, orders, staff setups & payments\n📊 **Reports** — Get details on sales and system analytics\n\nHow can I support your restaurant operations today?`;
      } else if (userRole === 'Staff' || userRole === 'Waiter' || userRole === 'Cashier' || userRole === 'Manager') {
        welcomeText = `${greet}, **${name}**! 👋 I'm **Maya**, your AI Team Assistant.\n\nI can guide you on daily tasks:\n📋 **Orders** — How to take new orders or run the POS terminal\n🪑 **Tables** — Real-time table indicators & seating floor layouts\n💳 **Payment** — Billing, UPI payments, and receipt printing\n\nWhat guidance do you need for this shift?`;
      }

      pushBot(welcomeText, 'FEATURES');
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const visibleGuideChips = GUIDE_CHIPS.filter(c => !c.roles || c.roles.includes(userRole));

  // ── Push helpers ─────────────────────────────────────────────────────────────
  function pushBot(text: string, intent: string, extra?: Partial<Message>) {
    setMessages(prev => [...prev, {
      id: Date.now().toString() + Math.random(),
      role: 'bot', text, intent, ts: new Date(), ...extra,
    }]);
  }

  function pushUser(text: string) {
    setMessages(prev => [...prev, {
      id: Date.now().toString() + Math.random(),
      role: 'user', text, ts: new Date(),
    }]);
  }

  // ── Add-menu flow ─────────────────────────────────────────────────────────────
  async function startAddMenuFlow() {
    if (!canAddMenu) {
      pushBot('⚠️ Only **Admins** can add menu items.', 'UNKNOWN');
      return;
    }
    if (!currentRestaurantId) {
      pushBot('⚠️ No restaurant selected. Please select a restaurant first.', 'UNKNOWN');
      return;
    }
    setAddStep('ask_name');
    setDraft({ name: '', price: 0, categoryId: '', categoryName: '', tags: '' });
    pushBot(
      '➕ **Add New Menu Item**\n\nLet\'s set it up step by step!\n\n**Step 1/4 — Name**\nWhat is the name of the dish?',
      'ADD_MENU'
    );
    setShowFood(false);
  }

  async function handleAddMenuInput(text: string) {
    pushUser(text);

    if (text.trim().toLowerCase() === 'cancel') {
      setAddStep('idle');
      pushBot('❌ Cancelled. No menu item was added.', 'ADD_MENU');
      return;
    }

    if (addStep === 'ask_name') {
      const name = text.trim();
      if (!name) { pushBot('Please enter a valid dish name.', 'ADD_MENU'); return; }
      setDraft(d => ({ ...d, name }));
      setAddStep('ask_price');
      pushBot(`Got it! **"${name}"** 🍽️\n\n**Step 2/4 — Price**\nWhat is the price in ₹? (Enter a number)`, 'ADD_MENU');
      return;
    }

    if (addStep === 'ask_price') {
      const price = parseFloat(text.trim());
      if (isNaN(price) || price <= 0) {
        pushBot('Please enter a valid price (e.g. 150 or 299.50).', 'ADD_MENU');
        return;
      }
      setDraft(d => ({ ...d, price }));

      // Fetch categories
      setLoading(true);
      try {
        const cats = await apiService.getMenuCategories(currentRestaurantId!);
        setCategories(cats);
        setAddStep('ask_category');
        if (cats.length === 0) {
          pushBot(
            `₹${price} — noted! 💰\n\n**Step 3/4 — Category**\n⚠️ No categories found for this restaurant.\n\nPlease create at least one category in the **Menu** section first, then try again.`,
            'ADD_MENU'
          );
          setAddStep('idle');
        } else {
          pushBot(
            `₹${price} — noted! 💰\n\n**Step 3/4 — Category**\nWhich category does this item belong to?\n\nTap a category below or type the name:`,
            'ADD_MENU',
            { chipOptions: cats.map(c => ({ label: c.name, value: c.id })) }
          );
        }
      } catch {
        pushBot('❌ Could not fetch categories. Please try again.', 'ADD_MENU');
        setAddStep('ask_price');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (addStep === 'ask_category') {
      // Allow text match against category names
      const match = categories.find(c =>
        c.name.toLowerCase().includes(text.trim().toLowerCase())
      );
      if (!match) {
        pushBot(
          `❓ Couldn't find that category. Please tap one of the options below:`,
          'ADD_MENU',
          { chipOptions: categories.map(c => ({ label: c.name, value: c.id })) }
        );
        return;
      }
      handleSelectCategory(match.id, match.name);
      return;
    }

    if (addStep === 'ask_tags') {
      const raw = text.trim().toLowerCase();
      const tags = raw === 'skip' ? '' : raw;
      setDraft(d => ({ ...d, tags }));
      setAddStep('confirm');
      showConfirmSummary({ ...draft, tags });
      return;
    }

    if (addStep === 'confirm') {
      const ans = text.trim().toLowerCase();
      if (ans === 'yes' || ans === 'y' || ans === 'confirm' || ans === 'ok' || ans === 'add') {
        await submitMenuItem();
      } else {
        setAddStep('idle');
        pushBot('❌ Cancelled. No menu item was added.\n\nType **add menu item** anytime to start again.', 'ADD_MENU');
      }
    }
  }

  function handleSelectCategory(catId: string, catName: string) {
    setDraft(d => ({ ...d, categoryId: catId, categoryName: catName }));
    setAddStep('ask_tags');
    pushBot(
      `**${catName}** — perfect! 🗂️\n\n**Step 4/4 — Tags**\nAdd tags to help customers find this item.\n\nExamples: \`spicy\`, \`veg\`, \`nonveg\`, \`dessert\`, \`drink\`, \`healthy\`, \`combo\`, \`kids\`\n\nType tags separated by commas, or type **skip** to leave blank.`,
      'ADD_MENU'
    );
  }

  function showConfirmSummary(data: AddMenuDraft) {
    pushBot(
      `✅ **Review your new menu item:**\n\n` +
      `📌 **Name:** ${data.name}\n` +
      `💰 **Price:** ₹${data.price}\n` +
      `🗂️ **Category:** ${data.categoryName}\n` +
      `🏷️ **Tags:** ${data.tags || '(none)'}\n\n` +
      `Type **yes** to add it, or **cancel** to abort.`,
      'ADD_MENU'
    );
  }

  async function submitMenuItem() {
    setLoading(true);
    try {
      await apiService.createMenuItem({
        name: draft.name,
        price: draft.price,
        categoryId: draft.categoryId,
        tags: draft.tags ? draft.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        isAvailable: true,
        restaurantId: currentRestaurantId,
      } as any);

      setAddStep('idle');
      pushBot(
        `🎉 **"${draft.name}"** has been added to the menu!\n\n` +
        `It's now live under **${draft.categoryName}** at ₹${draft.price}.\n\n` +
        `Want to add another item? Say **add menu item** anytime.`,
        'ADD_MENU'
      );
      setDraft({ name: '', price: 0, categoryId: '', categoryName: '', tags: '' });
    } catch (e: any) {
      pushBot(
        `❌ Failed to add menu item.\n${e?.response?.data?.message || e?.message || 'Please try again.'}`,
        'ADD_MENU'
      );
      setAddStep('idle');
    } finally {
      setLoading(false);
    }
  }

  // ── Main send handler ─────────────────────────────────────────────────────────
  async function sendMessage(text: string) {
    if (!text.trim() || loading) return;
    setInput('');

    // In an add-menu flow step, route to flow handler
    if (addStep !== 'idle') {
      await handleAddMenuInput(text);
      return;
    }

    // Intercept "add menu" trigger phrases locally (no backend round-trip)
    const lower = text.trim().toLowerCase();
    if (canAddMenu && (
      lower.includes('add menu') || lower.includes('add item') ||
      lower.includes('add dish') || lower.includes('add food') ||
      lower.includes('new item') || lower.includes('new dish') ||
      lower.includes('create item') || lower.includes('create dish')
    )) {
      pushUser(text);
      await startAddMenuFlow();
      return;
    }

    if (!currentRestaurantId) {
      pushUser(text);
      pushBot('⚠️ No restaurant selected. Please select a restaurant first.', 'UNKNOWN');
      return;
    }

    pushUser(text);
    setLoading(true);
    try {
      const { data: resp } = await apiService.post<{ message: string; intent: string; items: RecommendedItem[] }>(
        '/chat',
        { message: text, restaurantId: currentRestaurantId }
      );

      // If backend returns HOW_MENU intent and user is admin, nudge them towards direct add
      const extraNote = resp.intent === 'HOW_MENU' && canAddMenu
        ? '\n\n💡 _Pro tip: You can also say **"add menu item"** and I\'ll add it for you directly!_'
        : '';

      setMessages(prev => [...prev, {
        id: Date.now().toString() + Math.random(),
        role: 'bot',
        text: resp.message + extraNote,
        intent: resp.intent,
        items: resp.items,
        ts: new Date(),
      }]);
      if (!open) setUnread(n => n + 1);
    } catch {
      pushBot('❌ Could not reach the server. Please try again.', 'UNKNOWN');
    } finally {
      setLoading(false);
    }
  }

  function handleChipClick(chip: typeof GUIDE_CHIPS[0]) {
    if (chip.action === '__ADD_MENU__') {
      pushUser('Add a new menu item');
      startAddMenuFlow();
    } else {
      sendMessage(chip.msg);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  // ── Markdown renderer ─────────────────────────────────────────────────────────
  function md(text: string) {
    return text.split('\n').map((line, i, arr) => {
      const trimmed = line.trim();
      const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');
      
      if (isBullet) {
        let bulletText = trimmed.substring(1).trim();
        // Strip outer quotes if present
        if (bulletText.startsWith('"') && bulletText.endsWith('"')) {
          bulletText = bulletText.substring(1, bulletText.length - 1);
        }
        
        return (
          <Box
            key={i}
            onClick={() => sendMessage(bulletText)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: accent,
              fontWeight: 600,
              fontSize: '0.82rem',
              py: 0.5,
              px: 1.2,
              my: 0.5,
              borderRadius: '8px',
              bgcolor: isDark ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.05)',
              border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
              transition: 'all 0.15s',
              width: 'fit-content',
              '&:hover': {
                bgcolor: isDark ? 'rgba(99, 102, 241, 0.22)' : 'rgba(99, 102, 241, 0.1)',
                borderColor: accent,
                transform: 'translateX(3px)',
              },
            }}
          >
            • {bulletText}
          </Box>
        );
      }

      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <React.Fragment key={i}>
          {parts.map((p, j) =>
            j % 2 === 1
              ? <strong key={j}>{p}</strong>
              : p.includes('`')
                ? <>{p.split(/`(.*?)`/g).map((seg, k) =>
                    k % 2 === 1
                      ? <Box component="code" key={k} sx={{ px: 0.5, py: 0.1, borderRadius: 1, bgcolor: isDark ? '#334155' : '#e2e8f0', fontSize: '0.78rem', fontFamily: 'monospace' }}>{seg}</Box>
                      : seg
                  )}</>
                : <React.Fragment key={j}>{p}</React.Fragment>
          )}
          {i < arr.length - 1 && <br />}
        </React.Fragment>
      );
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  const inFlow = addStep !== 'idle';
  const inputPlaceholder = inFlow
    ? addStep === 'ask_name'     ? 'Type dish name…'
    : addStep === 'ask_price'    ? 'Enter price in ₹…'
    : addStep === 'ask_category' ? 'Type category name or select below…'
    : addStep === 'ask_tags'     ? 'e.g. spicy, veg, dessert  (or skip)'
    : addStep === 'confirm'      ? 'Type yes to confirm or cancel…'
    : 'Ask me anything…'
    : 'Ask me anything…';

  return (
    <>
      {/* ── FAB ──────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 1300,
        /* Hide when drawer is open */
        opacity: open ? 0 : 1,
        transform: open ? 'scale(0.6)' : 'scale(1)',
        pointerEvents: open ? 'none' : 'auto',
        transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      }}>
        <Tooltip title="Maya — AI Assistant" placement="left">
          <Badge badgeContent={unreadCount} color="error" overlap="circular">
            <Fab
              onClick={() => setOpen(true)}
              sx={{
                width: 60, height: 60,
                background: `linear-gradient(135deg, ${accent} 0%, #8b5cf6 100%)`,
                boxShadow: `0 8px 32px ${accent}55`,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'scale(1.08)', boxShadow: `0 12px 40px ${accent}88` },
                overflow: 'hidden'
              }}
            >
              <Avatar src="/images/chatbot_avatar_female.png" className="maya-avatar-float maya-avatar-hoverable" sx={{ width: 60, height: 60 }} />
            </Fab>
          </Badge>
        </Tooltip>
      </Box>

      {/* ── Drawer ──────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => { setOpen(false); if (inFlow) { setAddStep('idle'); } }}
        BackdropProps={{
          sx: {
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            background: 'transparent !important',
            backgroundColor: 'transparent !important',
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: PANEL_WIDTH },
            background: 'transparent',
            backdropFilter: 'blur(20px) saturate(190%)',
            WebkitBackdropFilter: 'blur(20px) saturate(190%)',
            borderLeft: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: isDark ? '-8px 0 32px rgba(0, 0, 0, 0.5)' : '-8px 0 32px rgba(31, 38, 135, 0.08)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          background: inFlow
            ? 'rgba(16, 185, 129, 0.18)'
            : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
          transition: 'background 0.4s',
        }}>
          <Avatar 
            src={inFlow ? undefined : "/images/chatbot_avatar_female.png"} 
            className={inFlow ? "" : (loading ? "maya-avatar-thinking" : "maya-avatar-float maya-avatar-hoverable")}
            sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)', width: 40, height: 40 }}
          >
            {inFlow && <AddCircle sx={{ color: textPri, fontSize: 22 }} />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                {inFlow ? 'Adding Menu Item…' : 'Maya — AI Assistant'}
              </Typography>
              {!inFlow && (
                <Chip
                  label="Guide"
                  size="small"
                  onClick={() => setShowFoodPanel(p => !p)}
                  sx={{
                    height: 18, fontSize: '0.65rem', cursor: 'pointer', fontWeight: 700,
                    bgcolor: showFoodPanel ? `${accent}33` : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'),
                    color: showFoodPanel ? accent : textPri,
                    border: `1px solid ${showFoodPanel ? accent+'55' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)')}`,
                    backdropFilter: 'blur(8px)',
                    '&:hover': { bgcolor: `${accent}22`, borderColor: accent+'66', color: accent },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
              <Box className="maya-status-dot" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80' }} />
              <Typography sx={{ color: textMut, fontSize: '0.72rem' }}>
                {inFlow
                  ? `Step: ${addStep.replace('ask_', '').replace('_', ' ')}`
                  : 'Online • MenuMaster AI'}
              </Typography>
            </Box>
          </Box>
          {inFlow && (
            <Tooltip title="Cancel adding item">
              <IconButton
                size="small"
                onClick={() => { setAddStep('idle'); pushBot('❌ Menu item creation cancelled.', 'ADD_MENU'); }}
                sx={{ color: textMut, mr: 0.5 }}
              >
                <Cancel sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => setOpen(false)} sx={{ color: textMut, '&:hover': { color: textPri } }}>
            <Close />
          </IconButton>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Side Food Types Panel */}
          {showFoodPanel && !inFlow && (
            <Box sx={{
              width: 120, flexShrink: 0, overflowY: 'auto',
              borderRight: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              px: 0.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5,
              '&::-webkit-scrollbar': { width: '3px' },
              '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', borderRadius: '2px' },
            }}>
              <Typography sx={{ fontSize: '0.62rem', color: textMut, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, px: 0.5 }}>
                Food Types
              </Typography>
              {FOOD_CHIPS.map(chip => (
                <Box key={chip.label} onClick={() => sendMessage(chip.msg)}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                    px: 0.5, py: 1, borderRadius: '10px', cursor: 'pointer',
                    bgcolor: 'transparent', transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.08)',
                      color: accent,
                    },
                  }}>
                  <Box sx={{ fontSize: 18, color: accent }}>{chip.icon}</Box>
                  <Typography sx={{ fontSize: '0.65rem', color: textPri, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                    {chip.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Right Column: Chat layout */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Flow progress bar */}
            {inFlow && (
              <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${border}`, flexShrink: 0, bgcolor: surface }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {(['ask_name', 'ask_price', 'ask_category', 'ask_tags', 'confirm'] as AddMenuStep[]).map((step, i) => {
                const steps: AddMenuStep[] = ['ask_name', 'ask_price', 'ask_category', 'ask_tags', 'confirm'];
                const done = steps.indexOf(addStep) > i;
                const active = addStep === step;
                return (
                  <Box key={step} sx={{
                    flex: 1, height: 4, borderRadius: 2,
                    bgcolor: done ? green : active ? `${green}88` : (isDark ? '#334155' : '#e2e8f0'),
                    transition: 'all 0.3s',
                  }} />
                );
              })}
            </Box>
            <Typography sx={{ fontSize: '0.68rem', color: textMut, mt: 0.5 }}>
              {addStep === 'ask_name' ? 'Step 1 of 4 — Dish Name'
               : addStep === 'ask_price' ? 'Step 2 of 4 — Price'
               : addStep === 'ask_category' ? 'Step 3 of 4 — Category'
               : addStep === 'ask_tags' ? 'Step 4 of 4 — Tags'
               : 'Confirm & Add'}
            </Typography>
          </Box>
        )}

        {/* Messages */}
        <Box sx={{ 
          flex: 1, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.15)', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.3)' },
        }}>
          <AnimatePresence initial={false}>
            {messages.map(msg => (
              <motion.div key={msg.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}>
                {msg.role === 'user' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      maxWidth: '72%', px: 2, py: 1.25,
                      borderRadius: '20px 20px 4px 20px',
                      background: isDark ? 'rgba(124, 92, 246, 0.3)' : 'rgba(99, 102, 241, 0.85)', // Colored bubble always readable
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: isDark ? '1px solid rgba(255, 255, 255, 0.22)' : '1px solid rgba(99, 102, 241, 0.3)',
                      boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255,255,255,0.15)',
                      color: '#ffffff', fontSize: '0.85rem', lineHeight: 1.5, wordBreak: 'break-word',
                      fontWeight: 500,
                    }}>
                      {msg.text}
                    </Box>
                    <Avatar 
                      sx={{ 
                        width: 32, height: 32, flexShrink: 0, mt: 0.3,
                        background: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(99, 102, 241, 0.15)',
                        backdropFilter: 'blur(8px)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.18)' : '1px solid rgba(99, 102, 241, 0.2)',
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      <Person sx={{ fontSize: 18, color: isDark ? '#ffffff' : '#6366f1' }} />
                    </Avatar>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar 
                      src={msg.intent === 'ADD_MENU' ? undefined : "/images/chatbot_avatar_female.png"} 
                      className="maya-avatar-hoverable"
                      sx={{ 
                        width: 32, height: 32, flexShrink: 0, mt: 0.3,
                        background: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)',
                        backdropFilter: 'blur(8px)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.18)' : '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.12)',
                        bgcolor: msg.intent === 'ADD_MENU' ? green : 'transparent' 
                      }}
                    >
                      {msg.intent === 'ADD_MENU' && <AddCircle sx={{ fontSize: 18, color: '#fff' }} />}
                    </Avatar>
                    <Box sx={{ maxWidth: 'calc(100% - 48px)' }}>
                      {msg.intent && msg.intent !== 'UNKNOWN' && (
                        <Chip
                          label={msg.intent === 'ADD_MENU' ? '➕ ADD ITEM' : msg.intent.replace('HOW_', 'HOW: ').replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            mb: 0.75, height: 18, fontSize: '0.62rem', fontWeight: 700,
                            background: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)',
                            color: textPri,
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                          }}
                        />
                      )}
                      <Box sx={{
                        px: 2, py: 1.25, borderRadius: '4px 20px 20px 20px',
                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)', // Frosted dark glass in light mode
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${msg.intent === 'ADD_MENU' ? 'rgba(16, 185, 129, 0.3)' : (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(15, 23, 42, 0.12)')}`,
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.12), inset 0 1px 1px rgba(255,255,255,0.05)',
                        fontSize: '0.84rem', color: textPri, lineHeight: 1.6, wordBreak: 'break-word',
                      }}>
                        {md(msg.text)}
                      </Box>

                      {/* Category selection chips (inline in message) */}
                      {msg.chipOptions && msg.chipOptions.length > 0 && addStep === 'ask_category' && (
                        <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {msg.chipOptions.map(opt => (
                            <Chip
                              key={opt.value}
                              label={opt.label}
                              size="small"
                              onClick={() => {
                                pushUser(opt.label);
                                handleSelectCategory(opt.value, opt.label);
                              }}
                              sx={{
                                height: 28, fontSize: '0.76rem', cursor: 'pointer',
                                bgcolor: 'rgba(16, 185, 129, 0.15)',
                                color: '#34d399',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                backdropFilter: 'blur(8px)',
                                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.25)', borderColor: '#34d399' },
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {/* Confirm action buttons */}
                      {addStep === 'confirm' && msg.intent === 'ADD_MENU' && msg.text.includes('Review your new menu item') && (
                        <Box sx={{ mt: 0.75, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={async () => { pushUser('Yes, add it!'); await submitMenuItem(); }}
                            disabled={loading}
                            sx={{
                              borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                              bgcolor: green, color: '#fff', px: 2,
                              '&:hover': { bgcolor: '#059669' },
                              '&.Mui-disabled': { bgcolor: isDark ? '#334155' : '#e2e8f0' },
                            }}
                          >
                            ✅ Add Item
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => { pushUser('Cancel'); setAddStep('idle'); pushBot('❌ Cancelled.', 'ADD_MENU'); }}
                            sx={{
                              borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                              bgcolor: isDark ? '#334155' : '#fee2e2',
                              color: isDark ? '#f87171' : '#dc2626',
                              '&:hover': { bgcolor: isDark ? '#451a1a' : '#fecaca' },
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}

                      {/* Recommended food items */}
                      {msg.items && msg.items.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {msg.items.map(item => (
                            <Box key={item.id} sx={{
                              px: 1.5, py: 1, borderRadius: '12px',
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.12)',
                              backdropFilter: 'blur(8px)',
                              display: 'flex', alignItems: 'center', gap: 1,
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)', borderColor: 'rgba(255, 255, 255, 0.3)' },
                              transition: 'all 0.15s',
                            }}>
                              <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <RestaurantMenu sx={{ fontSize: 18, color: '#ffffff' }} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#ffffff', lineHeight: 1.2 }}>{item.name}</Typography>
                                {item.tags && (
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.5)', mt: 0.2 }}>
                                    {item.tags.split(',').slice(0, 3).join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#8b5cf6', flexShrink: 0 }}>₹{item.price}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Avatar 
                src={inFlow ? undefined : "/images/chatbot_avatar_female.png"} 
                className="maya-avatar-thinking"
                sx={{ 
                  width: 32, height: 32, flexShrink: 0,
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.12)',
                  bgcolor: inFlow ? green : 'transparent' 
                }}
              >
                {inFlow && <AddCircle sx={{ fontSize: 18, color: '#fff' }} />}
              </Avatar>
              <Box sx={{ 
                px: 2, py: 1.25, borderRadius: '4px 20px 20px 20px', 
                background: 'rgba(255, 255, 255, 0.08)', 
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.12)',
                display: 'flex', gap: 0.5, alignItems: 'center' 
              }}>
                {[0, 0.15, 0.3].map((d, i) => (
                  <Box key={i} className="maya-dot-bounce" sx={{
                    width: 6, height: 6, borderRadius: '50%', bgcolor: inFlow ? green : accent,
                    animationDelay: `${d}s`,
                  }} />
                ))}
              </Box>
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>

        {/* Persistent category picker strip — shown whenever we are in ask_category step */}
        {addStep === 'ask_category' && categories.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)', bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.68rem', color: textMut, fontWeight: 700, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              📂 Select a category
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {categories.map(cat => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  size="small"
                  onClick={() => { pushUser(cat.name); handleSelectCategory(cat.id, cat.name); }}
                  sx={{
                    height: 30, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600,
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                    color: textPri,
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
                    '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)', borderColor: textPri },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Quick-action buttons — Skip (tags step) or Yes/Cancel (confirm step) */}
        {(addStep === 'ask_tags' || addStep === 'confirm') && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', gap: 1, flexShrink: 0, bgcolor: 'transparent' }}>
            {addStep === 'ask_tags' && (
              <Button
                size="small"
                onClick={() => { pushUser('skip'); sendMessage('skip'); }}
                disabled={loading}
                sx={{
                  borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
                }}
              >
                ⏭ Skip Tags
              </Button>
            )}
            {addStep === 'confirm' && (
              <>
                <Button
                  size="small"
                  startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                  onClick={async () => { pushUser('Yes, add it!'); await submitMenuItem(); }}
                  disabled={loading}
                  sx={{
                    borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                    bgcolor: green, color: '#fff', px: 2,
                    '&:hover': { bgcolor: '#059669' },
                    '&.Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  ✅ Add Item
                </Button>
                <Button
                  size="small"
                  startIcon={<Cancel sx={{ fontSize: 16 }} />}
                  onClick={() => { pushUser('Cancel'); setAddStep('idle'); pushBot('❌ Cancelled.', 'ADD_MENU'); }}
                  disabled={loading}
                  sx={{
                    borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                    bgcolor: 'rgba(220, 38, 38, 0.15)',
                    color: '#f87171',
                    border: '1px solid rgba(220, 38, 38, 0.3)',
                    '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.25)' },
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Input */}
        <Box sx={{ px: 2, py: 2, borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)', display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0, background: 'transparent' }}>
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            multiline maxRows={3} fullWidth size="small"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px', fontSize: '0.85rem', 
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                backdropFilter: 'blur(8px)',
                '& fieldset': { borderColor: inFlow ? 'rgba(16, 185, 129, 0.3)' : (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)') },
                '&:hover fieldset': { borderColor: inFlow ? '#34d399' : (isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.2)') },
                '&.Mui-focused fieldset': { borderColor: inFlow ? '#34d399' : (isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.2)') },
              },
              '& .MuiInputBase-input': { color: textPri, '&::placeholder': { color: textMut } },
            }}
          />
          <IconButton
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            sx={{
              width: 40, height: 40,
              background: input.trim() && !loading
                ? inFlow
                  ? `linear-gradient(135deg, ${green}, #059669)`
                  : `linear-gradient(135deg, ${accent}, #8b5cf6)`
                : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
              color: input.trim() && !loading ? '#fff' : textMut,
              borderRadius: '12px', flexShrink: 0, transition: 'all 0.2s',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
              '&:hover': { transform: 'scale(1.05)', bgcolor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)' },
              '&.Mui-disabled': { color: textMut, border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'}` },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: textMut }} /> : <Send sx={{ fontSize: 18, color: input.trim() ? '#ffffff' : textMut }} />}
          </IconButton>
        </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Animation keyframes are defined globally in index.css */}
    </>
  );
};

export default ChatbotFAB;
